/**
 * The Engine page's registration log (Other Charges registered on Ejar): who registered what and when, with the keys
 * of the rows written so a re-upload of the same rows is flagged before it is sent again.
 *
 *   Firestore   engine_registrations/<auto id>   { created_at (Timestamp), user_uid, user_email, ejar_user, ... }
 *   the store   /api/store/ejar-registrations     (USE_CONTRACTS_V2, owner 27 Sep 2026: "we will not rely on Firebase
 *               anymore") the same fields; `created_at` is ISO text, and who / when are the engine's (the signed-in
 *               account, its clock) - the page sends neither. Deleting an entry is for admins only there.
 *               With FINES_WRITE_PG the engine logs a job's entry itself; `logAfterJob` then writes none (one row a job).
 */
import { addDoc, collection, deleteDoc, doc, getDocs, limit, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';

const COL = 'engine_registrations';
const viaStore = (call) => call().catch((e) => { throw officeError(e); });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** When an entry was written, as a Date (a Firestore Timestamp or the store's ISO text); null when unknown. */
export const registrationTime = (entry) => {
    const v = entry?.created_at;
    if (!v) return null;
    if (typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};

export const engineRegistrations = {
    /** The newest entries first: [{id, ...fields}]. A failed read throws. */
    async recent(max = 30) {
        if (USE_CONTRACTS_V2) return viaStore(() => finesService.storeEjarRegistrations({ limit: max }));
        const snap = await getDocs(query(collection(db, COL), orderBy('created_at', 'desc'), limit(max)));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },

    /** One entry: `fields` = what was registered (file, rows, counts, written_keys ...); `user` = the signed-in user. */
    async log(fields, user) {
        if (USE_CONTRACTS_V2) return viaStore(() => finesService.storeEjarRegistrationAdd(fields));
        return addDoc(collection(db, COL), {
            created_at: serverTimestamp(), last_updated: serverTimestamp(),
            user_uid: user?.uid || '', user_email: user?.email || '',
            ...fields,
        });
    },

    /**
     * After an Other-Charges job: its entry, written ONCE. With FINES_WRITE_PG the engine logs the registration
     * itself on the store at the job's end (from the `registration` the page sent with the commit): the commit's
     * answer (`start`) says `registrationByEngine` and the job's `registration` is {byEngine, logged, id | error}. So
     * on the store the page waits a little for that log (the engine writes it just after the job reads done) and
     * posts its own entry only when the engine says the log failed (`error`) - posting it too left two history rows.
     * Firestore (flag off): the page's own entry as before (an engine entry is on the store, where it does not read).
     * `readJob()` reads the job again. Returns 'page' (the page wrote it), 'engine' (the engine logged it) or
     * 'pending' (the engine has not logged it yet and will: the page writes no second one).
     */
    async logAfterJob({ start, job, fields, user, readJob, tries = 5, intervalMs = 1000, wait = sleep }) {
        if (!USE_CONTRACTS_V2 || !start?.registrationByEngine) {
            await this.log(fields, user);
            return 'page';
        }
        let reg = job?.registration;
        for (let i = 0; i < tries && !reg?.logged && !reg?.error; i += 1) {
            await wait(intervalMs);
            try {
                reg = (await readJob())?.registration || reg;
            } catch (e) { /* the job is gone or the engine blinked: one try less, never a second entry */ }
        }
        if (reg?.logged) return 'engine';
        if (!reg?.error) return 'pending';
        await this.log(fields, user);
        return 'page';
    },

    /** Delete one entry (the charges on Ejar are not touched). */
    async remove(id) {
        if (USE_CONTRACTS_V2) return viaStore(() => finesService.storeEjarRegistrationDelete(id));
        return deleteDoc(doc(db, COL, id));
    },
};

export default engineRegistrations;
